import React, { useState, useRef, useEffect } from "react";
import ChevronIcon from "./SVGIcons/ChevronIcon";

const DashboardTimelineSelector = ({ onChange }) => {
  const options = [
    "Today",
    "Yesterday",
    "All Time",
    "Custom Date",
    "Last Week",
    "This Week",
    "Last Month",
    "This Month",
    "Last 7 Days",
    "Last 30 Days",
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(options[0]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isHoveredHeader, setIsHoveredHeader] = useState(false);

  const dropdownRef = useRef(null);
  const dropdownOptionsRef = useRef(null);

  const styles = {
    dropdownContainer: {
      position: "relative",
      width: "160px",
      border: "1px solid #ddd",
      borderRadius: "6px",
      height: "30px",
    },
    dropdownHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 12px",
      backgroundColor: "white",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "border-color 0.2s ease",
      height: "100%",
    },
    dropdownHeaderHover: {
      borderColor: "#999",
    },
    selectedValue: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#111",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    chevronIcon: {
      transition: "transform 0.2s ease",
      display: "flex",
    },
    chevronIconRotate: {
      transform: "rotate(180deg)",
    },
    dropdownMenu: {
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      width: "100%",
      backgroundColor: "white",
      border: "1px solid #ddd",
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      zIndex: 1000,
    },
    dropdownOptions: {
      maxHeight: "350px",
      overflowY: "auto",
    },
    optionItem: {
      padding: "8px 12px",
      fontSize: "14px",
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    optionItemHover: {
      backgroundColor: "#f0f0f0",
    },
    optionItemSelected: {
      backgroundColor: "#f0f0f0",
      fontWeight: 600,
    },
  };

  const handleSelect = (value) => {
    setSelectedValue(value);
    onChange(value);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const scrollToOption = (index) => {
    const optionsContainer = dropdownOptionsRef.current;
    if (!optionsContainer) return;

    const optionElements = optionsContainer.children;
    if (index < 0 || index >= optionElements.length) return;

    const optionElement = optionElements[index];
    const containerTop = optionsContainer.scrollTop;
    const containerBottom = containerTop + optionsContainer.clientHeight;
    const elementTop = optionElement.offsetTop;
    const elementBottom = elementTop + optionElement.clientHeight;

    if (elementTop < containerTop) {
      optionsContainer.scrollTop = elementTop;
    } else if (elementBottom > containerBottom) {
      optionsContainer.scrollTop =
        elementBottom - optionsContainer.clientHeight;
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prevIndex) => {
          const newIndex = prevIndex === options.length - 1 ? 0 : prevIndex + 1;
          scrollToOption(newIndex);
          return newIndex;
        });
        break;

      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prevIndex) => {
          const newIndex = prevIndex === 0 ? options.length - 1 : prevIndex - 1;
          scrollToOption(newIndex);
          return newIndex;
        });
        break;

      case "Enter":
        if (focusedIndex >= 0) {
          handleSelect(options[focusedIndex]);
        }
        break;

      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div style={styles.dropdownContainer} ref={dropdownRef}>
      <div
        style={{
          ...styles.dropdownHeader,
          ...(isHoveredHeader ? styles.dropdownHeaderHover : {}),
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHoveredHeader(true)}
        onMouseLeave={() => setIsHoveredHeader(false)}
      >
        <span style={styles.selectedValue}>{selectedValue}</span>
        <div
          style={{
            ...styles.chevronIcon,
            ...(isOpen ? styles.chevronIconRotate : {}),
          }}
        >
          <ChevronIcon />
        </div>
      </div>

      {isOpen && (
        <div style={styles.dropdownMenu}>
          <div ref={dropdownOptionsRef} style={styles.dropdownOptions}>
            {options.map((option, index) => (
              <div
                key={index}
                style={{
                  ...styles.optionItem,
                  ...(focusedIndex === index ? styles.optionItemHover : {}),
                  ...(selectedValue === option
                    ? styles.optionItemSelected
                    : {}),
                }}
                onMouseEnter={() => setFocusedIndex(index)}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTimelineSelector;
